import { useEffect, useState } from "react";
import { Camera, CircleCheck, FileText, FileUp, Info, X } from "lucide-react";
import { PassoBar, QrTesta } from "./QrTesta";

interface BollettaProps {
  variante: "bolletta";
  files: File[];
  onChange: (files: File[]) => void;
}

interface DocumentoProps {
  variante: "documento";
  fronte: File | null;
  retro: File | null;
  onFronte: (f: File) => void;
  onRetro: (f: File) => void;
}

type Props = { agenzia: string; onAvanti: () => void } & (BollettaProps | DocumentoProps);

/** Schermate «Bolletta» e «Documento» (spec §8), parametrizzate per variante:
 *  stesso involucro (testata, barra dei passi, «Avanti»), contenuto diverso. */
export function QrFoto(props: Props) {
  const { agenzia, onAvanti } = props;
  const passo = props.variante === "bolletta" ? 1 : 2;
  const testoPasso = props.variante === "bolletta" ? "1 di 3 · Bolletta" : "2 di 3 · Documento";
  const puoAvanti = props.variante === "bolletta"
    ? props.files.length > 0
    : Boolean(props.fronte && props.retro);

  return (
    <div className="min-h-dvh flex flex-col bg-surface">
      <QrTesta agenzia={agenzia} />
      <div className="w-full max-w-md mx-auto flex-1 flex flex-col">
      <PassoBar passo={passo} testo={testoPasso} />
      <div className="p-6 pt-3 flex-1 flex flex-col">
        {props.variante === "bolletta" ? (
          <>
            <h1 className="font-heading text-2xl font-bold leading-tight">La bolletta</h1>
            <p className="text-lg mt-2">Tutte le pagine, una foto per pagina. Va bene anche il file PDF.</p>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <SceltaFile
                testId="bolletta-fotocamera"
                accept="image/*"
                capture="environment"
                onScelti={(picked) => props.onChange([...props.files, ...picked])}
                className="h-28 rounded-xl bg-brand text-white flex flex-col items-center justify-center gap-2 text-lg font-semibold"
              >
                <Camera className="w-8 h-8" aria-hidden="true" />
                {/* Dopo la prima foto il pulsante dice cosa fa adesso (Alfonso 2026-09-29,
                    modello B): prima non era chiaro che servisse anche per la pagina 2. */}
                {props.files.length > 0 ? "Altra pagina" : "Fotocamera"}
              </SceltaFile>
              <SceltaFile
                testId="bolletta-file"
                accept="application/pdf,image/*"
                multiple
                onScelti={(picked) => props.onChange([...props.files, ...picked])}
                className="h-28 rounded-xl bg-card border-2 border-border flex flex-col items-center justify-center gap-2 text-lg font-semibold"
              >
                <FileUp className="w-8 h-8 text-brand" aria-hidden="true" />
                File PDF
              </SceltaFile>
            </div>
            {props.files.length > 0 && (
              <>
                <p className="text-lg text-muted-foreground mt-4">
                  Ha altre pagine? Tocchi &quot;Altra pagina&quot; per ognuna.
                </p>
                <ul className="mt-4 space-y-2">
                  {props.files.map((f, i) => (
                    <PaginaBolletta
                      key={`${f.name}-${f.size}-${f.lastModified}-${i}`}
                      file={f}
                      numero={i + 1}
                      onTogli={() => props.onChange(props.files.filter((_x, j) => j !== i))}
                    />
                  ))}
                </ul>
              </>
            )}
          </>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold leading-tight">Il documento d&apos;identità</h1>
            <p className="text-lg mt-2">Carta d&apos;identità, patente o passaporto: fronte e retro.</p>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <RiquadroLato testId="documento-fronte" label="Fronte" file={props.fronte} onScatta={props.onFronte} />
              <RiquadroLato testId="documento-retro" label="Retro" file={props.retro} onScatta={props.onRetro} />
            </div>
            <p className="text-lg text-muted-foreground mt-4 flex gap-2">
              <Info className="w-5 h-5 shrink-0" aria-hidden="true" />
              Il documento serve al fornitore per il contratto.
            </p>
          </>
        )}
        <button
          type="button"
          onClick={onAvanti}
          disabled={!puoAvanti}
          className="mt-auto h-14 rounded-xl bg-brand text-white text-lg font-semibold disabled:opacity-40"
        >
          Avanti
        </button>
      </div>
      </div>
    </div>
  );
}

/** Pulsante che apre fotocamera o file con un campo «scegli file» VERO dentro la pagina,
 *  nascosto nel pulsante: il tocco lo apre da sé. Prima il campo si creava al volo fuori
 *  dalla pagina e si premeva da programma, e su iPhone il pulsante a volte non reagiva
 *  affatto (prova di Alfonso del 2026-09-29). */
function SceltaFile({
  testId, accept, capture, multiple, onScelti, className, children,
}: {
  testId: string;
  accept: string;
  capture?: "environment";
  multiple?: boolean;
  onScelti: (files: File[]) => void;
  className: string;
  children: React.ReactNode;
}) {
  return (
    <label data-testid={testId} className={`${className} cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand has-[:focus-visible]:ring-offset-2`}>
      <input
        type="file"
        accept={accept}
        capture={capture}
        multiple={multiple}
        className="sr-only"
        onChange={(e) => {
          const scelti = Array.from(e.target.files ?? []);
          e.target.value = ""; // la stessa foto si può riscegliere
          if (scelti.length) onScelti(scelti);
        }}
      />
      {children}
    </label>
  );
}

/** Indirizzo temporaneo per mostrare una foto scelta (null per i PDF o senza file),
 *  rilasciato quando la foto cambia o il riquadro sparisce. */
function useAnteprima(file: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file || !file.type.startsWith("image/") || typeof URL.createObjectURL !== "function") {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

/** Una pagina della bolletta: miniatura (le foto) o icona (i PDF), e la X per toglierla
 *  se è venuta male. La X è un bersaglio di 44×44 con un nome leggibile dal lettore
 *  dello schermo. */
function PaginaBolletta({ file, numero, onTogli }: { file: File; numero: number; onTogli: () => void }) {
  const anteprima = useAnteprima(file);
  return (
    <li className="flex items-center gap-3 bg-card border border-border rounded-xl py-2 pl-3 pr-1">
      {anteprima ? (
        <img src={anteprima} alt="" className="w-11 h-14 object-cover rounded-md border border-border" />
      ) : (
        <FileText className="w-6 h-6 text-muted-foreground" aria-hidden="true" />
      )}
      <span className="text-lg">Pagina {numero}</span>
      <CircleCheck className="w-6 h-6 text-brand ml-auto" aria-hidden="true" />
      <button
        type="button"
        onClick={onTogli}
        aria-label={`Togli la pagina ${numero}`}
        className="w-11 h-11 flex items-center justify-center rounded-lg text-muted-foreground"
      >
        <X className="w-6 h-6" aria-hidden="true" />
      </button>
    </li>
  );
}

function RiquadroLato({
  testId, label, file, onScatta,
}: { testId: string; label: string; file: File | null; onScatta: (f: File) => void }) {
  const scatta = (picked: File[]) => { if (picked[0]) onScatta(picked[0]); };
  const anteprima = useAnteprima(file);
  if (file) {
    // L'intero riquadro (già ≥48px per lato) è il tasto per rifare lo scatto — non solo
    // la scritta "Nuova foto", troppo piccola come bersaglio. Con la miniatura della foto
    // (Alfonso 2026-09-29: senza, non si vedeva se il documento era venuto bene).
    return (
      <SceltaFile
        testId={testId}
        accept="image/*,application/pdf"
        capture="environment"
        onScelti={scatta}
        className="h-44 rounded-xl bg-card border-2 border-brand flex flex-col items-center justify-center gap-1.5 p-2"
      >
        {anteprima ? (
          <img src={anteprima} alt="" className="h-20 w-full object-contain rounded-md" />
        ) : (
          <CircleCheck className="w-8 h-8 text-brand" aria-hidden="true" />
        )}
        <span className="text-lg font-semibold flex items-center gap-1.5">
          {anteprima && <CircleCheck className="w-5 h-5 text-brand" aria-hidden="true" />}
          {label}
        </span>
        <span className="text-lg text-muted-foreground underline">Nuova foto</span>
      </SceltaFile>
    );
  }
  return (
    <SceltaFile
      testId={testId}
      accept="image/*,application/pdf"
      capture="environment"
      onScelti={scatta}
      className="h-36 rounded-xl bg-card border-2 border-dashed border-border flex flex-col items-center justify-center gap-2"
    >
      <Camera className="w-8 h-8 text-brand" aria-hidden="true" />
      <span className="text-lg font-semibold">{label}</span>
    </SceltaFile>
  );
}
